// REACT IMPORT: Core React library for component creation
import React, { useState } from "react";
import { BookOpen, ListChecks, Star } from "lucide-react";

// SHADCN/UI IMPORTS: Modern UI components for enhanced recipe cards
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "./components/ui/card";
import { Button } from "./components/ui/button";

// STYLING: Now using Tailwind CSS classes and shadcn/ui components
// All styles have been converted to modern component-based architecture

// RECIPE COMPONENT: Displays individual recipe information in a card format
// This is a functional component using props destructuring for cleaner code

// PROPS DESTRUCTURING: Extract specific props from the props object
// {title, calories, image, ingredients, url} instead of props.title, props.calories, etc.
const Recipe = ({
  title,
  calories,
  image,
  ingredients,
  url,
  isPremium = false,
  isSaved = false,
  isSaving = false,
  onToggleSave
}) => {
  const formattedCalories = Math.round(calories).toLocaleString()
  const [showSavedEffect, setShowSavedEffect] = useState(false)

  const handleSaveClick = async () => {
    const shouldCelebrate = !isSaved


    if (shouldCelebrate) {
      setShowSavedEffect(false)
      window.requestAnimationFrame(() => {
        setShowSavedEffect(true)
        window.setTimeout(() => setShowSavedEffect(false), 1200)
      })
    }

    await onToggleSave?.()
  }
  
  // COMPONENT RENDER: Enhanced glass morphism card with sophisticated styling
  return (
    <Card className="group relative mx-auto my-4 flex h-[640px] w-[300px] shrink-0 flex-col overflow-visible rounded-2xl border border-white/30 bg-glass-light shadow-[0_22px_45px_rgba(25,15,10,0.24),0_10px_24px_rgba(255,107,53,0.2)] backdrop-blur-lg transition-all duration-500 hover:-translate-y-2 hover:shadow-[0_34px_70px_rgba(25,15,10,0.34),0_0_55px_rgba(255,107,53,0.5)] sm:mx-4 sm:my-6 sm:h-[720px] sm:w-[360px] sm:rounded-3xl md:h-[820px] md:w-[420px]">

      {/* Background decoration */}
      <div className="absolute top-0 right-0 h-24 w-24 rounded-full bg-orange-400/10 opacity-0 blur-3xl transition-opacity duration-500 group-hover:opacity-100 sm:h-32 sm:w-32"></div>
      
      {/* Enhanced Card Header with improved layout */}
      <CardHeader className="relative z-30 shrink-0 space-y-0 overflow-visible p-3 pb-2 text-center sm:space-y-1.5 sm:p-6 sm:pb-6">
        {/* Enhanced Recipe Image with overlay */}
        <div className="relative mb-2 h-40 shrink-0 sm:mb-3 sm:h-52 md:h-64">
          <div className="absolute inset-0 overflow-hidden rounded-xl shadow-[0_18px_35px_rgba(25,15,10,0.28),0_8px_18px_rgba(255,107,53,0.22)] ring-2 ring-white/25 transition-all duration-500 group-hover:-translate-y-1 group-hover:shadow-[0_28px_55px_rgba(25,15,10,0.34),0_0_42px_rgba(255,107,53,0.48)] group-hover:ring-orange-200/80 sm:rounded-2xl">
            <img
              className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
              src={image}
              alt={`${title} recipe`}
            />
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-orange-400/0 via-transparent to-orange-600/0 opacity-0 transition-opacity duration-500 group-hover:from-orange-400/15 group-hover:to-orange-600/20 group-hover:opacity-100"></div>
            {/* Gradient overlay for better text readability */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100"></div>

            {/* Floating calorie badge */}
            <div className="group/calorie absolute bottom-2.5 left-2.5 rounded-xl border border-white/30 bg-glass-warm px-2.5 py-1 shadow-modern backdrop-blur-md transition-all duration-300 hover:scale-110 hover:border-orange-200 hover:bg-white/95 hover:shadow-[0_14px_34px_rgba(25,15,10,0.34),0_0_28px_rgba(255,107,53,0.32)] sm:bottom-4 sm:left-4 sm:rounded-2xl sm:px-4 sm:py-2">
              <span className="text-sm font-bold text-orange-600 sm:text-lg">{formattedCalories}</span>
              <span className="ml-1 inline-block min-w-[2.25rem] text-left text-[11px] text-warm-gray-700 sm:min-w-[3.75rem] sm:text-sm">
                <span className="group-hover/calorie:hidden">cal</span>
                <span className="hidden group-hover/calorie:inline">calories</span>
              </span>
            </div>
          </div>

          {/* Premium save button - overlaid so it never changes card height */}
          {isPremium && (
            <div className="absolute right-2.5 top-2.5 z-40 sm:right-4 sm:top-4">
              {showSavedEffect && (
                <div className="pointer-events-none absolute bottom-full left-1/2 z-50 mb-2 -translate-x-1/2 sm:mb-2.5">
                  <div className="saved-recipe-label whitespace-nowrap rounded-full bg-white px-3 py-1 text-xs font-bold text-orange-600 shadow-modern ring-1 ring-orange-100">
                    Saved!
                  </div>
                </div>
              )}
              {showSavedEffect && (
                <div className="saved-recipe-confetti pointer-events-none absolute inset-0 z-10 overflow-visible" aria-hidden="true">
                  {Array.from({ length: 14 }).map((_, index) => (
                    <span key={index} className="saved-recipe-confetti-piece" />
                  ))}
                </div>
              )}
              <Button
                type="button"
                variant="ghost"
                size="icon"
                disabled={isSaving}
                onClick={handleSaveClick}
                aria-label={isSaved ? `Remove saved recipe: ${title}` : `Save recipe: ${title}`}
                title={isSaved ? 'Remove saved recipe' : 'Save recipe'}
                className={`relative z-20 h-9 w-9 rounded-xl border shadow-modern backdrop-blur-md transition-all duration-300 hover:scale-110 sm:h-12 sm:w-12 sm:rounded-2xl ${
                  isSaved
                    ? "border-yellow-300 bg-yellow-100/95 text-yellow-500 shadow-[0_10px_28px_rgba(234,179,8,0.32)] hover:bg-yellow-100 hover:text-yellow-600"
                    : "border-yellow-300 bg-white/90 text-yellow-500 shadow-[0_10px_26px_rgba(120,75,20,0.22)] hover:border-yellow-400 hover:bg-yellow-50 hover:text-yellow-600 hover:shadow-[0_12px_30px_rgba(234,179,8,0.26)]"
                }`}
              >
                <Star
                  className={isSaved ? "h-4 w-4 fill-current sm:h-6 sm:w-6" : "h-4 w-4 sm:h-6 sm:w-6"}
                  aria-hidden="true"
                  strokeWidth={isSaved ? 2.75 : 2.8}
                />
              </Button>
            </div>
          )}
        </div>

        {/* Enhanced Recipe Title - fixed 2-line block keeps every card equal height */}
        <CardTitle className="mx-auto mb-0 flex h-12 w-full items-center justify-center px-1 text-center text-lg font-bold leading-tight text-warm-charcoal transition-colors duration-300 group-hover:text-white sm:mb-2 sm:h-auto sm:min-h-[3.5rem] sm:text-2xl md:text-3xl">
          <span className="line-clamp-2">{title}</span>
        </CardTitle>

      </CardHeader>
      
      {/* Enhanced Card Content with better styling */}
      <CardContent className="flex min-h-0 flex-1 flex-col px-3 pt-0 sm:px-6">
        {/* Recipe section header */}
        <div className="mb-1.5 flex h-7 shrink-0 items-center justify-center sm:mb-3 sm:h-auto">
          <h4 className="inline-flex items-center justify-center gap-1.5 rounded-full bg-orange-50 px-3 py-1 text-xs font-semibold text-orange-600 shadow-sm ring-1 ring-orange-100 sm:gap-2 sm:px-5 sm:py-2 sm:text-lg">
            <BookOpen className="h-3.5 w-3.5 sm:h-5 sm:w-5" aria-hidden="true" strokeWidth={2.25} />
            Recipe
          </h4>
        </div>

        {/* Enhanced Ingredients section header */}
        <div className="mb-2.5 flex h-7 shrink-0 items-center justify-center sm:mb-4 sm:h-auto">
          <h4 className="inline-flex items-center justify-center gap-1.5 rounded-full bg-orange-50 px-3 py-1 text-xs font-semibold text-orange-600 shadow-sm ring-1 ring-orange-100 sm:gap-2 sm:px-5 sm:py-2 sm:text-lg">
            <ListChecks className="h-3.5 w-3.5 sm:h-5 sm:w-5" aria-hidden="true" strokeWidth={2.25} />
            Ingredients
          </h4>
        </div>

        {/* Enhanced ingredients list with better styling */}
        <div className="custom-scrollbar min-h-0 flex-1 overflow-y-auto pr-1.5 sm:pr-2">
          <ul className="space-y-1.5 text-left sm:space-y-3">
            {ingredients.map((ingredient, index) => (
              <li
                key={`${ingredient.text}-${index}`}
                className="group/item flex items-start gap-2 text-sm leading-snug text-warm-gray-700 sm:gap-3 sm:text-xl sm:leading-relaxed"
                style={{ animationDelay: `${index * 50}ms` }}
              >
                <span className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-white transition-colors duration-300 sm:mt-2 sm:h-2 sm:w-2"></span>
                <span className="transition-colors duration-300 group-hover/item:text-white">
                  {ingredient.text}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </CardContent>
      
      {/* Enhanced Card Footer with sophisticated button */}
      <CardFooter className="mt-auto flex h-[3.75rem] shrink-0 items-center justify-center px-3 pb-3 pt-2 sm:h-auto sm:px-6 sm:pb-6 sm:pt-6">
        <Button
          asChild
          className="group/button h-9 w-full transform rounded-xl border-0 bg-gradient-to-r from-orange-500 to-orange-600 text-sm font-bold text-white shadow-modern transition-all duration-300 hover:-translate-y-1 hover:from-orange-600 hover:to-orange-700 hover:text-white hover:shadow-glow sm:h-12 sm:rounded-2xl sm:text-base"
        >
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex w-full items-center justify-center gap-1.5 whitespace-nowrap text-white hover:text-white sm:gap-2"
          >
            <span className="whitespace-nowrap">See Full Recipe</span>
            <span className="shrink-0 text-base transition-transform duration-300 group-hover/button:translate-x-1 sm:text-lg">→</span>
          </a>
        </Button>
      </CardFooter>

      {/* Hover effect border */}
      <div className="pointer-events-none absolute inset-0 z-0 overflow-hidden rounded-2xl border-2 border-orange-400/0 ring-0 ring-orange-200/0 transition-all duration-500 group-hover:border-orange-300/50 group-hover:ring-4 group-hover:ring-orange-200/30 sm:rounded-3xl"></div>
    </Card>
  );
}

// EXPORT: Make component available for import in other files
export default Recipe;
